import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../../schema'
import { Col, Row } from 'react-bootstrap'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../../common/integrations'
import { MappingType, OperationType } from '../../../../../../../models/Account'
import { useFilterIntegrationMappingTemplatesQuery, useGetSupportedObjectsQuery } from '../../../../../../../store/integrations/integrations'
import { useMemo } from 'react'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../../store/hooks'
import { useGetIntegrationAdminSettings } from '../../../hooks'
import { useGetSchemaErrors } from '../../../schemaErrors'

interface SelectDocumentTypeSectionProps {
  onChange: () => void
  serviceId: IntegrationServiceId
}

export const SelectDocumentTypeSection: React.FC<SelectDocumentTypeSectionProps> = ({ onChange, serviceId }) => {
  const { watch, setValue } = useFormContext<IntegrationSettings>()
  const accountId = useAppSelector(selectAccountId)
  const currentObjectName = watch('mappings.documentSummary.objectName')
  const currentMappingType = watch('mappings.documentSummary.mappingType')
  const isCustom = useMemo(() => currentMappingType === MappingType.CUSTOM, [currentMappingType])

  const { data: outboundMappingTemplatePaths, isFetching: isOutboundMappingTemplatePathsFetching } = useFilterIntegrationMappingTemplatesQuery({
    active: true,
    destinationPlatform: serviceId,
    sourceObject: 'documentSummary',
  })

  const documentCRMTypeOptions: SelectOption[] = useMemo(() => {
    if (!outboundMappingTemplatePaths) {
      return []
    }
    return outboundMappingTemplatePaths.map((i) => ({ id: i.destinationObject, label: i.name }))
  }, [outboundMappingTemplatePaths])

  const { hasIntegration } = useGetIntegrationAdminSettings(serviceId)

  const { data: supportedObjects, isLoading: isLoadingSupportedObjects } = useGetSupportedObjectsQuery(
    { accountId, connector: serviceId },
    { skip: !accountId || !serviceId || !hasIntegration },
  )

  const customDocumentOption = useMemo(() => supportedObjects?.find((opt) => opt.name === currentObjectName) || null, [supportedObjects, currentObjectName])

  const predefinedDocumentOption: SelectOption | null = useMemo(() => {
    if (isCustom) {
      return { id: 'customDocumentObject', label: 'Custom Document Object' }
    }
    return documentCRMTypeOptions.find((opt) => opt.id === currentObjectName) || null
  }, [documentCRMTypeOptions, currentObjectName, isCustom])

  const customDocumentOptions: SelectOption[] = useMemo(() => {
    return supportedObjects?.map((obj) => ({ id: obj.name, label: `${obj.label} (${obj.name})` })) || []
  }, [supportedObjects])

  const handleCustomOptionSelect = (option: { id: string; label: string }) => {
    if (option.id === currentObjectName && isCustom) {
      return
    }
    setValue('mappings.documentSummary', { objectName: option.id, operationType: OperationType.CREATE }, { shouldDirty: true, shouldValidate: true })
    setValue('mappings.documentSummary.mappingType', MappingType.CUSTOM, {
      shouldDirty: true,
    })
    onChange()
  }

  const handlePredefinedOptionSelect = (option: { id: string; label: string }) => {
    if (option.id === currentObjectName && !isCustom) {
      return
    }

    if (option.id === 'customDocumentObject') {
      setValue('mappings.documentSummary', { objectName: null }, { shouldDirty: true, shouldValidate: true })
      setValue('mappings.documentSummary.mappingType', MappingType.CUSTOM, {
        shouldDirty: true,
        shouldValidate: true,
      })
      onChange()

      return
    }
    setValue('mappings.documentSummary', { objectName: option.id }, { shouldDirty: true, shouldValidate: true })
    setValue('mappings.documentSummary.mappingType', MappingType.PREDEFINED, {
      shouldDirty: true,
      shouldValidate: true,
    })
    onChange()
  }

  const { getErrorOnPath } = useGetSchemaErrors()
  const fieldError = getErrorOnPath('mappings.documentSummary.objectName')
  const objectError = getErrorOnPath('mappings.documentSummary')

  return (
    <>
      <Row className='mb-2'>
        <Col>
          <h4>Select Object Type*</h4>
          <p className='zocks-text-gray-600'>Select the {getIntegrationDisplayName(serviceId)} object where Document Intelligence will be saved.</p>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <SelectDropdown
            initialSelectedOption={predefinedDocumentOption}
            options={documentCRMTypeOptions}
            onOptionSelected={handlePredefinedOptionSelect}
            loading={isOutboundMappingTemplatePathsFetching}
          />
          {fieldError && <div className='text-danger mt-1 small'>{fieldError}</div>}
          {objectError && <div className='text-danger mt-1 small'>{objectError}</div>}
        </Col>
        {isCustom && (
          <Col md={6}>
            <SelectDropdown
              options={customDocumentOptions}
              initialSelectedOption={
                customDocumentOption ? { id: customDocumentOption.name, label: `${customDocumentOption.label} (${customDocumentOption.name})` } : null
              }
              placeholder='Select Object Type'
              onOptionSelected={handleCustomOptionSelect}
              searchable
              loading={isLoadingSupportedObjects}
            />
          </Col>
        )}
      </Row>
    </>
  )
}
